import {
  Box3,
  Mesh,
  MeshPhongMaterial,
  Vector3
} from "three";
import { IFCLoader } from "web-ifc-three";
import { boundsToMapLayer, roundDigit } from "../utilities";
import { LineworkFile, CADHandler, LineworkHandler } from ".";
import { readFileBuffer } from "../file-system-loader";
import { isDevMode } from "../../../electron-modules";
import { toast } from "../../../app";
import { Asset } from "../../../redux/assets-slice";
import LocalScene from "../projections";
import { getBasicPicker } from "../gpu-picker";
import { RayCaster } from "../ray-caster";
import { t } from "../../../localization";

export class IFCFile extends LineworkFile {
  public standardMesh: Mesh;
  public pickingMesh: Mesh;
  private loader: IFCLoader;
  private useWebWorkers = true;

  private highlightMaterial = new MeshPhongMaterial({
    color: 0xff00ff,
    depthTest: false,
    transparent: true,
    opacity: 0.3
  });

  constructor(ifcHandler: IFCHandler, asset: Asset) {
    super(ifcHandler, asset);
    this.loader = new IFCLoader();
  }

  get manager() {
    return this.loader.ifcManager;
  }

  get ifcAPI() {
    return this.manager.ifcAPI;
  }

  async load() {
    this.loading = true;
    this.loaded = false;
    this.updatePercent(0);

    // Initialize IFC manager
    await this.initManager();

    try {
      const buffer = await readFileBuffer(this.path);
      if (!this.enabled) return;

      await this.parseFromBuffer(buffer);
    } catch {
      this.error = true;
      toast.error(t("toast.model-load-error", {
        name: this.name
      }));
    }

    this.loaded = true;
    this.loading = false;
    this.updatePercent(100.0);

    if (this.saved || this.error) return;
    this.viewer.zoomToSceneExtent(this.boundingBox, false);
  }

  async parseFromBuffer(buffer) {
    return new Promise(async (resolve, reject) => {
      /** Note: requires modified code in ifc-worker.js */
      if (this.useWebWorkers) {
        const handler = (this.manager as any).worker;
        handler.ifcWorker.onmessage = (event) => {
          const {action, error} = event.data;
          if (action === "error") {
            reject(error);
            return;
          };

          handler.handleResponse(event);
        };
      }

      const mesh = await this.loader.parse(buffer);
      await this.addSceneData(mesh);
      await this.addAerialData();

      resolve(true);
    });
  }

  async addAerialData() {
    // Aerial view layer
    this.mapLayer = boundsToMapLayer(this.boundingBox);
    this.map.addLayer(this.mapLayer);

    // Apply current visibility
    this.mapLayer.setVisible(this.visible);
  }

  async addSceneData(mesh) {
    // Apply mesh scene rotation
    mesh.rotation.set(Math.PI/2, 0, 0);

    // Apply mesh coordinate shift
    const modelID = mesh.modelID;
    const coordinationMatrix = await this.ifcAPI.GetCoordinationMatrix(modelID);

    const offset = new Vector3()
      .fromArray(coordinationMatrix.slice(12, 15))
      .applyEuler(mesh.rotation)
      .multiplyScalar(-1);

    // Convert mesh to meters
    mesh.scale.setScalar(LocalScene.dataToMeters);
    offset.multiplyScalar(LocalScene.dataToMeters);

    // Move model to scene coordinates
    const shift = LocalScene.offsetToScene(offset);
    mesh.position.copy(shift);

    // Scene view mesh
    this.standardMesh = mesh;
    this.scene.add(this.standardMesh);

    // Picking scene mesh
    this.pickingMesh = getBasicPicker(mesh, Mesh);
    this.picker.add(this.pickingMesh);

    // Apply current visibility
    this.standardMesh.visible = this.visible;
    this.pickingMesh.visible = this.visible;

    // Update bounding box
    this.boundingBox = new Box3()
      .setFromObject(this.standardMesh);
  }

  async initManager() {
    if (this.useWebWorkers) {
      const workerURL = "workers/ifc-worker.js";
      const workerPath = `${process.env.PUBLIC_URL}/${workerURL}`;
      await this.manager.useWebWorkers(true, workerPath);
    } else {
      await this.manager.useWebWorkers(false, null);
    }

    const wasmPath = isDevMode
      ? "../../workers/"
      : "";

    await this.manager.setWasmPath(wasmPath);

    this.manager.applyWebIfcConfig({
      COORDINATE_TO_ORIGIN: true,
      USE_FAST_BOOLS: true
    });

    this.manager.setOnProgress(event => {
      let percent = event.loaded / event.total;
      this.updatePercent(100 * percent);
    });
  }

  /** Update visibility */
  updateProperties(asset: Asset) {
    const state = asset.visible;
    this.visible = state;

    if (this.mapLayer) {
      this.mapLayer.setVisible(state);
    }

    if (this.standardMesh) {
      this.standardMesh.visible = state;
    }

    if (this.pickingMesh) {
      this.pickingMesh.visible = state;
    }

    this.updatePicker();
  }

  removeFromScene(item) {
    if (!item) return;
    this.picker.remove(item);
    this.scene.remove(item);
  }

  removeSubset() {
    const ifcMesh = this.standardMesh;
    if (!ifcMesh) return;

    const modelID = ifcMesh['modelID'];
    this.manager.removeSubset(modelID, this.highlightMaterial);
  }

  /** @private */
  attributesToProperties(attributes) {
    const properties = [];

    for (let [key, data] of Object.entries(attributes)) {
      let value = (data as any)?.value;
      if (!value) {
        continue;
      }

      if (typeof value === "number") {
        value = roundDigit(value, 10);
      }

      properties.push({property: key, value});
    };

    return properties;
  }

  async getProperties(faceIndex) {
    const ifcMesh = this.standardMesh;
    const modelID = ifcMesh['modelID'];
    const geometry = ifcMesh.geometry;

    const expressID = this.manager.getExpressId(geometry, faceIndex);
    const attributes = await this.manager.getItemProperties(
      modelID, expressID, true);

    const properties = this.attributesToProperties(attributes);

    const subsetMesh = this.manager.createSubset({
      modelID,
      ids: [ expressID ],
      scene: this.scene,
      removePrevious: true,
      material: this.highlightMaterial
    });

    subsetMesh.rotation.copy(ifcMesh.rotation);
    subsetMesh.position.copy(ifcMesh.position);
    subsetMesh.scale.copy(ifcMesh.scale);

    return properties;
  }

  disposeGeometry(item) {
    if (!item) return;
    item.geometry.dispose();
    item.material.forEach(material => {
      material.dispose();
    });
  }

  destroy() {
    this.enabled = false;

    this.removeSubset();

    this.removeFromScene(this.standardMesh);
    this.disposeGeometry(this.standardMesh);
    this.standardMesh = null;

    this.removeFromScene(this.pickingMesh);
    this.disposeGeometry(this.pickingMesh);
    this.pickingMesh = null;

    this.removeMapLayer();
    this.mapLayer = null;

    this.manager.dispose()
      .catch(err => {
        console.log(err);
      });
  }
}

export class IFCHandler extends LineworkHandler {
  protected files: IFCFile[] = [];

  constructor(cadHandler: CADHandler) {
    super(cadHandler);
  }

  get objects() {
    return this.files
      .map(file => file.standardMesh)
      .filter(mesh => mesh !== null);
  }

  async getProperties(event) {
    const raycaster = new RayCaster(this.viewer, event);
    const objects = this.objects.filter(mesh => mesh && mesh.visible);
    const intersects = raycaster.intersectObjects(objects);
    if (intersects.length === 0) return [];

    const intersect = intersects[0];
    const selectedFile = this.files.find((file: IFCFile) => {
      return file.standardMesh === intersect.object;
    });

    if (!selectedFile) return [];

    const attributes = await selectedFile
      .getProperties(intersect.faceIndex);

    return attributes;
  }

  clearHighlight() {
    this.files.forEach(file => {
      file.removeSubset();
    });
  }
}
